import {
  Box,
  Button,
  Grid,
  IconButton,
  Stack,
  styled,
  Typography
} from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import { Plus, Users } from "react-feather";
import { RewardsForm } from "../../components/moduleForms/RewardsRecognition/RewardsForm";
import { resolveRenewalPeriod } from "../../components/onboarding/TierInfoBox";
import { ColouredContainer } from "../../components/shared/ColoredContainer";
import { LoadingSpinner } from "../../components/shared/LoadingSpinner";
import { PageHeader } from "../../components/shared/PageHeader";
import { SideDrawer } from "../../components/shared/SideDrawer";
import { TabGroup } from "../../components/shared/TabGroup";
import { RewardsSentTable } from "../../components/table/Rewards/RewardsSentTable";
import { PageHeaderControlContext } from "../../context/PageHeaderControlContext";
import { useDeletePlan } from "../../hooks/plans/useDeletePlan";
import { MembershipType, Plan } from "../../models";
import { AccountKind } from "../../models/accounts/accountKind";
import { EmployerModuleType } from "../../models/accounts/employerModuleType";
import { extraordinaryColors } from "../../utils/theme";
import { useData } from "../../utils/useData";
import {
  DeletePlanButton,
  PlanStatistics,
  StyledEditIcon
} from "../allowances/Allowances";

const UsersIcon = styled(Users)({ color: "white", marginRight: 8 });

const ContributionBox = styled(Box)({
  border: `2px solid ${extraordinaryColors.blue.main}`,
  borderRadius: "4px",
  marginTop: "8px",
  paddingRight: "12px",
  paddingLeft: "6px",
  paddingTop: "6px",
  paddingBottom: "6px",
  textAlign: "right"
});
export const RewardReceivingStatistics = ({
  totalUsers
}: {
  totalUsers: Number;
}) => {
  const totalUserDisplay = `${totalUsers} Receiving Users`;

  return (
    <Box display={"flex"} flexDirection={"row"} paddingY={2}>
      <UsersIcon />
      <Typography marginRight={2}>{totalUserDisplay}</Typography>
    </Box>
  );
};

const Rewards = () => {
  const { setHeaderActions } = useContext(PageHeaderControlContext);
  const { plans, membership, accounts } = useData();
  const [isAddingAllocation, setIsAddingAllocation] = useState<boolean>(false);
  const [editingAllocation, setEditingAllocation] = useState<
    Plan | undefined
  >();

  const isPilot = membership.data?.type === MembershipType.PILOT;
  const planTotalItems = plans?.data?.totalItems ?? 0;
  const canAddPlans = isPilot
    ? true
    : membership.data?.features?.maxPlanCount
    ? planTotalItems >= membership.data?.features?.maxPlanCount
    : false;

  const planTotalUsersMap: Record<string, number> = useMemo(() => {
    const planMap: Record<string, number> = {};
    accounts.data?.items
      .filter(
        (a) =>
          a.employerModuleOptions.employerModuleType ===
          EmployerModuleType.REWARDS_RECOGNITION
      )
      .forEach((account) => {
        planMap[account.planId] ??= 0;
        planMap[account.planId] += 1;
      });

    return planMap;
  }, [accounts.data]);

  // An "allocation" is a plan that is only used for sending money (payments).
  const renderAllocation = (plan: Plan) => {
    const renewalPeriod = resolveRenewalPeriod(
      plan.renewalOptions.periodInMonths ?? 0
    );

    const receivingAccounts =
      planTotalUsersMap[
        plan.controls?.payments?.allowedTargetPlans?.[0] ?? ""
      ] ?? 0;

    return (
      <Grid item xs={12} md={6} key={plan.id}>
        <ColouredContainer
          title={plan.name}
          color={extraordinaryColors.blue.main}
          toolbarItems={
            <>
              <DeletePlanButton
                totalUsers={
                  (planTotalUsersMap[plan.id] ?? 0) + receivingAccounts
                }
                onClick={() => mutationDeleteAllocation.mutate(plan)}
              />
              <IconButton onClick={() => setEditingAllocation(plan)}>
                <StyledEditIcon />
              </IconButton>
            </>
          }
          subtitleItems={
            <Stack direction="row" spacing={2}>
              <PlanStatistics totalUsers={planTotalUsersMap[plan.id] ?? 0} />
              <RewardReceivingStatistics totalUsers={receivingAccounts} />
            </Stack>
          }
        >
          <Box sx={{ display: "flex", flexDirection: "column", padding: 2 }}>
            <Typography fontSize={18} fontWeight={"bold"}>
              Contributions
            </Typography>
            {plan.tiers.map((tier) => (
              <>
                <ContributionBox>
                  <Typography>
                    ${tier.amount.amount}
                    {renewalPeriod !== undefined ? `/${renewalPeriod}` : null}
                  </Typography>
                </ContributionBox>
              </>
            ))}
          </Box>
        </ColouredContainer>
      </Grid>
    );
  };

  const mutationDeleteAllocation = useDeletePlan({
    successMessage: "Reward deleted successfully"
  });

  useEffect(() => {
    setHeaderActions([
      <Button
        variant="contained"
        onClick={() => setIsAddingAllocation(true)}
        startIcon={<Plus size={16} />}
        size="medium"
        disabled={canAddPlans}
      >
        New
      </Button>
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (plans.isLoading || plans.isIdle) {
    return <LoadingSpinner />;
  }

  if (plans.error || !plans.data) {
    return <p>Something went wrong, please try again later</p>;
  }

  return (
    <>
      <PageHeader pageTitle="Rewards" />
      <TabGroup tabLabels={["Recognition"]}>
        <Box>
          <Grid container spacing={2} my={1}>
            {plans.data.items
              .filter(
                (p) =>
                  p.employerModuleOptions.employerModuleType ===
                    EmployerModuleType.REWARDS_RECOGNITION &&
                  p.accountKind === AccountKind.REWARDS_SENDING
              )
              .map(renderAllocation)}
          </Grid>

          <RewardsSentTable />
        </Box>
      </TabGroup>

      <SideDrawer
        title="Add Reward"
        open={isAddingAllocation}
        setIsOpen={setIsAddingAllocation}
      >
        <RewardsForm onClose={() => setIsAddingAllocation(false)} />
      </SideDrawer>

      <SideDrawer
        title="Edit Reward"
        open={!!editingAllocation}
        setIsOpen={setEditingAllocation}
      >
        <RewardsForm
          plan={editingAllocation}
          onClose={() => setEditingAllocation(undefined)}
        />
      </SideDrawer>
    </>
  );
};

export { Rewards };
